import React, { useMemo } from 'react';
import { Button } from '~/lib/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/lib/ui/dialog';
import { cn } from '~/lib/utils';
import { useConfirmationModalStore } from '../../store';
import { convertMarkdownBold } from './utils';

export const SIZES = {
  default: { content: 'sm:max-w-lg', header: undefined, description: undefined },
  embed: {
    content: 'sm:max-w-[668px] w-full h-[548px] max-h-[90vh] flex flex-col gap-3 overflow-hidden',
    header: 'min-h-0 flex-1',
    description: 'flex-1 min-h-0 max-h-none overflow-hidden',
  },
} as const;

/**
 * ConfirmationModal component that displays a confirmation dialog for user actions.
 *
 * This component renders a modal dialog that asks users to confirm potentially destructive
 * actions.
 */
export const ConfirmationModal = (): React.ReactElement => {
  const {
    isOpen,
    testid,
    title,
    description,
    textConfirm,
    textCancel,
    preventCloseOnClickOutside,
    size,
    confirm,
    cancel,
  } = useConfirmationModalStore();

  const convertedDescription = useMemo(
    () =>
      typeof description === 'string' ? convertMarkdownBold(description) : null,
    [description]
  );

  if (!isOpen) return null;

  const handleInteractOutside = (e: Event) => {
    if (preventCloseOnClickOutside) {
      e.preventDefault();
    }
  };

  const showFooter = Boolean(textCancel || textConfirm);
  const sizeClasses = SIZES[size ?? 'default'];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && cancel()}>
      <DialogContent
        className={sizeClasses.content}
        data-testid={testid}
        onInteractOutside={handleInteractOutside}
      >
        <DialogHeader className={sizeClasses.header}>
          <DialogTitle
            className={title ? 'text-left text-xl' : 'sr-only'}
          >
            {title || 'Confirmation required'}
          </DialogTitle>

          <DialogDescription
            asChild={!!description}
            className={description
              ? 'text-left pt-2 text-foreground text-base'
              : 'sr-only'
            }
          >
            {description && (
              <div
                className={cn(
                  'break-words overflow-y-auto max-h-80',
                  sizeClasses.description
                )}
              >
                {convertedDescription ? (
                  <p dangerouslySetInnerHTML={{ __html: convertedDescription }} />
                ) : (

                  typeof description === 'string' ? (
                    <p className="break-words break-all">{description}</p>
                  ) : (
                    description
                  )
                )}
              </div>
            )}
          </DialogDescription>
        </DialogHeader>

        {showFooter && (
          <DialogFooter className="gap-5.5">
            {textCancel && (
              <Button
                size="lg"
                variant="outline"
                onClick={cancel}
                className="mt-3 sm:mt-0 bg-transparent"
                autoFocus={!textConfirm}
              >
                {textCancel}
              </Button>
            )}
            {textConfirm && (
              <Button size="lg" onClick={confirm}>
                {textConfirm}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog >
  );
};
